// SapUsagePeriodicPanel.jsx — tab "SAP Usage (Trimester)" on the Periodic Report page.
// Pick a trimester / month / year → preview the figures → download the .xlsx report.
import { useState, useEffect, useCallback } from 'react';
import { api as axiosApi } from '../context/AuthContext';

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmt = (n, d = 0) => (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
const mLabel = (m) => `${MON[+String(m).slice(5) - 1]} ${String(m).slice(0, 4)}`;

const card = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px' };
const lbl = { fontSize: 11, fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 4 };
const sel = { padding: '7px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, background: '#fff', minWidth: 220 };
const th = { textAlign: 'left', padding: '6px 8px', fontSize: 11.5, color: '#475569', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' };
const td = { padding: '6px 8px', fontSize: 12.5, borderBottom: '1px solid #f1f5f9' };
const tdn = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

function Btn({ children, onClick, disabled, primary }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      style={{ padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
        border: primary ? 'none' : '1px solid #cbd5e1', background: primary ? 'rgb(var(--brand-600))' : '#fff', color: primary ? '#fff' : '#0f172a' }}>
      {children}
    </button>
  );
}

export default function SapUsagePeriodicPanel() {
  const [opts, setOpts] = useState({ landscapes: [], trimesters: [], months: [] });
  const [kind, setKind] = useState('trimester');
  const [landscape, setLandscape] = useState('ID');
  const [period, setPeriod] = useState('');
  const [sum, setSum] = useState(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await axiosApi.get('/api/periodic-report/sap-usage/options');
        const o = r.data || {};
        setOpts({ landscapes: o.landscapes || [], trimesters: o.trimesters || [], months: o.months || [] });
        if (o.landscapes && o.landscapes.length && !o.landscapes.includes('ID')) setLandscape(o.landscapes[0]);
      } catch (e) { setErr((e.response && e.response.data && e.response.data.error) || e.message); }
    })();
  }, []);

  const choices = (() => {
    if (kind === 'trimester') return opts.trimesters.filter((t) => t.landscape === landscape).map((t) => ({ v: t.key, label: `${t.label}${t.complete ? '' : ' — incomplete'}` }));
    if (kind === 'month') return opts.months.filter((m) => m.landscape === landscape).map((m) => ({ v: m.key, label: mLabel(m.key) })).reverse();
    const ys = [...new Set(opts.months.filter((m) => m.landscape === landscape).map((m) => m.key.slice(0, 4)))].sort().reverse();
    return ys.map((y) => ({ v: y, label: `Year ${y}` }));
  })();
  const choiceKey = choices.map((c) => c.v).join(',');

  // Default: the latest COMPLETE trimester (or first choice)
  useEffect(() => {
    if (!choices.length) { setPeriod(''); return; }
    if (choices.some((c) => c.v === period)) return;
    const firstComplete = kind === 'trimester' ? opts.trimesters.find((t) => t.landscape === landscape && t.complete) : null;
    setPeriod(firstComplete ? firstComplete.key : choices[0].v);
  }, [choiceKey, kind, landscape]); // eslint-disable-line

  const loadSummary = useCallback(async () => {
    if (!period) return;
    setBusy('summary'); setErr('');
    try {
      const r = await axiosApi.get(`/api/periodic-report/sap-usage/summary?period=${encodeURIComponent(period)}&landscape=${encodeURIComponent(landscape)}`);
      setSum(r.data);
    } catch (e) { setSum(null); setErr((e.response && e.response.data && e.response.data.error) || e.message); }
    finally { setBusy(''); }
  }, [period, landscape]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const download = async (kindKey) => {
    const ep = kindKey === 'pivot' ? 'pivot-xlsx' : 'xlsx';
    setBusy(kindKey); setErr(''); setOk('');
    try {
      const res = await axiosApi.get(`/api/periodic-report/sap-usage/${ep}?period=${encodeURIComponent(period)}&landscape=${encodeURIComponent(landscape)}`, { responseType: 'blob' });
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      const name = (m && m[1]) || `SAP_User_Usage_${landscape}_${period}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      setOk(kindKey === 'pivot'
        ? `Downloaded ${name}. Excel refreshes every pivot when the file opens (click "Enable Editing" if it opens in Protected View), then pick the period on the Year / Months slicers.`
        : `Downloaded ${name}`);
    } catch (e) {
      let msg = e.message;
      try { if (e.response && e.response.data instanceof Blob) { const j = JSON.parse(await e.response.data.text()); msg = j.error || msg; } } catch (_) { /* keep message */ }
      setErr(msg);
    }
    finally { setBusy(''); }
  };

  const k = sum && sum.kpi;
  return (
    <div>
      <div style={{ fontSize: 12.5, color: '#475569', marginBottom: 12 }}>
        SAP User Usage report per <b>trimester</b> (T1 = Jan–Apr, T2 = May–Aug, T3 = Sep–Dec), month or year — the same sheets as the manual
        Excel analysis: summary, usage per period, monthly pivot, trimester comparison, average per working day, licensed users without usage, and raw data.
        Data comes from <b>Table Maintenance → SAP Usage</b> (fetched from SAP P01).
        <div style={{ marginTop: 6 }}>
          <b>Pivot workbook</b> = the original analysis workbook (pivot tables + slicers) refilled with all usage data from 2025 onwards;
          Excel rebuilds the pivots when the file is opened. <b>Quick summary</b> = plain tables for the selected period only.
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 14 }}>
        <div>
          <label style={lbl}>Report type</label>
          <select style={{ ...sel, minWidth: 140 }} value={kind} onChange={(e) => { setKind(e.target.value); setPeriod(''); }}>
            <option value="trimester">Trimester</option>
            <option value="month">Month</option>
            <option value="year">Full year</option>
          </select>
        </div>
        <div>
          <label style={lbl}>Period</label>
          <select style={sel} value={period} onChange={(e) => setPeriod(e.target.value)}>
            {!choices.length && <option value="">No data</option>}
            {choices.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
          </select>
        </div>
        {opts.landscapes.length > 1 && (
          <div>
            <label style={lbl}>Landscape</label>
            <select style={{ ...sel, minWidth: 90 }} value={landscape} onChange={(e) => { setLandscape(e.target.value); setPeriod(''); }}>
              {opts.landscapes.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        )}
        <Btn onClick={loadSummary} disabled={!period || !!busy}>{busy === 'summary' ? '⏳ Loading…' : '🔄 Refresh'}</Btn>
        <Btn primary onClick={() => download('pivot')} disabled={!period || !!busy}>{busy === 'pivot' ? '⏳ Building…' : '⬇️ Pivot workbook (.xlsx)'}</Btn>
        <Btn onClick={() => download('xlsx')} disabled={!period || !!busy}>{busy === 'xlsx' ? '⏳ Building…' : '⬇️ Quick summary (.xlsx)'}</Btn>
      </div>

      {err && <div style={{ ...card, background: '#fef2f2', borderColor: '#fecaca', color: '#b91c1c', marginBottom: 12, fontSize: 12.5 }}>{err}</div>}
      {ok && <div style={{ ...card, background: '#f0fdf4', borderColor: '#bbf7d0', color: '#166534', marginBottom: 12, fontSize: 12.5 }}>{ok}</div>}

      {sum && (
        <>
          {sum.missingMonths && sum.missingMonths.length > 0 && (
            <div style={{ ...card, background: '#fffbeb', borderColor: '#fde68a', color: '#92400e', marginBottom: 12, fontSize: 12.5 }}>
              ⚠️ No usage data yet for <b>{sum.missingMonths.map(mLabel).join(', ')}</b>. The report covers {sum.monthsWithData.map(mLabel).join(', ') || 'no month'} only.
              Fetch the missing months in Table Maintenance → SAP Usage once they have ended.
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 14 }}>
            {[
              ['Users with usage', fmt(k.users)],
              ['Total hours', fmt(k.totalHours)],
              ['Avg hours / user', fmt(k.avgHoursPerUser, 1)],
              ['Avg hours / user / day', fmt(k.avgDay, 2)],
              ['Licensed, no usage', fmt(k.idleLicensed)],
            ].map(([a, b]) => (
              <div key={a} style={card}>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>{a}</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginTop: 2 }}>{b}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 }}>
            <div style={card}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Per month</div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>Month</th><th style={{ ...th, textAlign: 'right' }}>Users</th><th style={{ ...th, textAlign: 'right' }}>Hours</th></tr></thead>
                <tbody>{sum.byMonth.map((m) => <tr key={m.period}><td style={td}>{mLabel(m.period)}</td><td style={tdn}>{fmt(m.users)}</td><td style={tdn}>{fmt(m.hours)}</td></tr>)}</tbody>
              </table>
            </div>
            <div style={card}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Per company</div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>Company</th><th style={{ ...th, textAlign: 'right' }}>Users</th><th style={{ ...th, textAlign: 'right' }}>Hours</th><th style={{ ...th, textAlign: 'right' }}>Avg h/day</th></tr></thead>
                <tbody>{sum.companies.map((c) => <tr key={c.company}><td style={td}>{c.company}</td><td style={tdn}>{fmt(c.users)}</td><td style={tdn}>{fmt(c.hours)}</td><td style={tdn}>{fmt(c.avgDay, 2)}</td></tr>)}</tbody>
              </table>
            </div>
            <div style={card}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Users per licence type</div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>Licence</th><th style={{ ...th, textAlign: 'right' }}>Users</th></tr></thead>
                <tbody>{sum.licenses.map((l) => <tr key={l.license}><td style={td}>{l.license}</td><td style={tdn}>{fmt(l.users)}</td></tr>)}</tbody>
              </table>
            </div>
            <div style={card}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Top 10 users by hours</div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>User</th><th style={th}>Company</th><th style={{ ...th, textAlign: 'right' }}>Hours</th><th style={{ ...th, textAlign: 'right' }}>Avg h/day</th></tr></thead>
                <tbody>{sum.topUsers.map((u) => <tr key={u.user}><td style={td} title={u.name}>{u.user}</td><td style={td}>{u.company}</td><td style={tdn}>{fmt(u.hours)}</td><td style={tdn}>{fmt(u.avgDay, 2)}</td></tr>)}</tbody>
              </table>
            </div>
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 10 }}>
            Connection {sum.connection || '-'} · user master snapshot {sum.snapshotAt ? sum.snapshotAt.slice(0, 10) : '-'} (SAP User Maintenance → Load Users) ·
            avg per day assumes 20 working days per month.
          </div>
        </>
      )}
    </div>
  );
}
